import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Edit as EditIcon } from '@mui/icons-material';
import TrustIndicator from '../components/readiness/TrustIndicator';
import ReadinessMatrix from '../components/readiness/ReadinessMatrix';
import TrustOverrideDialog from '../components/readiness/TrustOverrideDialog';
import { readinessConfidenceService } from '../services/readinessConfidenceService';

interface OverrideRecord {
  id: number;
  readiness?: string;
  confidence?: string;
  reason: string;
  overriddenBy: string;
  overriddenAt: string;
}

export default function ReadinessIndicatorPage() {
  const { experimentId } = useParams<{ experimentId: string }>();
  const [openOverride, setOpenOverride] = useState(false);

  if (!experimentId) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Experiment ID not found</Alert>
      </Container>
    );
  }

  const expId = parseInt(experimentId, 10);

  // Fetch trust indicator
  const indicatorQuery = useQuery({
    queryKey: ['trustIndicator', expId],
    queryFn: () => readinessConfidenceService.getTrustIndicator(expId),
    refetchInterval: 30000, // Refresh every 30s
  });

  // Fetch override history
  const historyQuery = useQuery({
    queryKey: ['overrideHistory', expId],
    queryFn: () => readinessConfidenceService.getOverrideHistory(expId, 20),
    enabled: !!expId,
  });

  // Override mutation
  const overrideMutation = useMutation({
    mutationFn: (data: { readiness?: string; confidence?: string; reason: string }) =>
      readinessConfidenceService.overrideTrustIndicator(expId, {
        readiness: data.readiness as any,
        confidence: data.confidence as any,
        reason: data.reason,
      }),
    onSuccess: () => {
      indicatorQuery.refetch();
      historyQuery.refetch();
      setOpenOverride(false);
    },
  });

  const indicator = indicatorQuery.data;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Trust Indicator - Experiment #{expId}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Readiness × Confidence Assessment
        </Typography>
      </Box>

      {indicatorQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : indicatorQuery.isError ? (
        <Alert severity="error">Failed to load trust indicator</Alert>
      ) : indicator ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Main Trust Indicator */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <TrustIndicator
                  readiness={indicator.readiness}
                  confidence={indicator.confidence}
                  readinessReason={indicator.readinessReason}
                  confidenceReason={indicator.confidenceReason}
                  readinessEvidenceLink={indicator.readinessEvidenceLink}
                  confidenceEvidenceLink={indicator.confidenceEvidenceLink}
                  interpretation={indicator.interpretation}
                  size="medium"
                  orientation="vertical"
                />
              </Box>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                onClick={() => setOpenOverride(true)}
                sx={{ alignSelf: 'flex-start' }}
              >
                Override
              </Button>
            </Box>
          </Paper>

          {/* Readiness × Confidence Matrix */}
          <Paper elevation={2} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Readiness × Confidence Matrix
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              This grid shows all possible combinations of readiness and confidence levels
            </Typography>
            <ReadinessMatrix />
          </Paper>

          {/* Override History */}
          {historyQuery.data && historyQuery.data.length > 0 && (
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Override History
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Readiness</TableCell>
                      <TableCell>Confidence</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Overridden By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyQuery.data.map((record: OverrideRecord) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <Typography variant="caption">
                            {new Date(record.overriddenAt).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {record.readiness && (
                            <Chip
                              label={record.readiness}
                              size="small"
                              variant="outlined"
                              color={
                                record.readiness === 'PASS'
                                  ? 'success'
                                  : record.readiness === 'WARN'
                                    ? 'warning'
                                    : 'error'
                              }
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {record.confidence && (
                            <Chip
                              label={record.confidence}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{record.reason}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{record.overriddenBy}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Empty override history state */}
          {historyQuery.data && historyQuery.data.length === 0 && (
            <Paper elevation={2} sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                No override history yet
              </Typography>
            </Paper>
          )}
        </Box>
      ) : null}

      {/* Override Dialog */}
      <TrustOverrideDialog
        open={openOverride}
        onClose={() => setOpenOverride(false)}
        currentReadiness={indicator?.readiness}
        currentConfidence={indicator?.confidence}
        onSubmitOverride={(data) => {
          overrideMutation.mutate(data);
        }}
        loading={overrideMutation.isPending}
      />
    </Container>
  );
}
